// ============================================
// API Client
// ============================================

import { useAuthStore } from '../stores/auth';

// Use Worker directly with credentials for cross-origin cookie support
const API_BASE = 'https://crm-sales-os-api.bawaya-2019.workers.dev/api';

interface RequestOptions extends RequestInit {
  params?: Record<string, string>;
}

class ApiClient {
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    const sessionToken = useAuthStore.getState().sessionToken;
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }
    
    return headers;
  }
  
  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    
    let url = `${API_BASE}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }
    
    const response = await fetch(url, {
      credentials: 'include',
      ...fetchOptions,
      headers: {
        ...this.getHeaders(),
        ...fetchOptions.headers,
      },
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Request failed' }));
      
      // Handle auth errors
      if (response.status === 401) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
      
      throw new Error(error.error?.message || error.message || 'Request failed');
    }
    
    return response.json();
  }
  
  get<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }
  
  post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
  
  patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
  
  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const api = new ApiClient();

// ============================================
// Auth API - Twilio Verify OTP
// ============================================

export const authApi = {
  // Start OTP verification (uses admin phone from backend)
  startAuth: (phone_e164?: string) => 
    api.post<{ 
      ok: boolean; 
      channel_used?: 'whatsapp' | 'sms';
      error?: string;
    }>('/auth/start', phone_e164 ? { phone_e164 } : {}),
  
  // Verify OTP code
  verifyAuth: (code: string, phone_e164?: string) => 
    api.post<{ 
      ok: boolean; 
      sessionToken: string; 
      user: { id: string; phone: string; name: string; role: string };
      error?: string;
    }>('/auth/verify', { code, ...(phone_e164 ? { phone_e164 } : {}) }),
  
  // PIN login (backup)
  loginWithPin: (pin: string) =>
    api.post<{
      ok: boolean;
      sessionToken?: string;
      user?: { id: string; phone: string; name: string; role: string };
      error?: string;
    }>('/auth/pin', { pin }),
  
  // Legacy - kept for compatibility
  requestOtp: (phone: string) => 
    authApi.startAuth(phone),
  
  verifyOtp: (phone: string, code: string) => 
    authApi.verifyAuth(code, phone),
  
  logout: () => api.post('/auth/logout'),
  
  logoutAll: () => api.post('/auth/logout-all'),
  
  getMe: () => api.get<{ ok: boolean; user: { id: string; phone: string; name: string; role: string } }>('/auth/me'),
};

// ============================================
// Leads API
// ============================================

export interface Lead {
  id: string;
  project_id: string;
  contact_id: string;
  stage: string;
  source: string;
  notes: string | null;
  entered_in_hot: boolean;
  created_at: string;
  updated_at: string;
  contact?: {
    id: string;
    phone_e164_masked: string;
    name_first: string;
    name_last: string;
    email?: string;
    city?: string;
    national_id_masked?: string;
  };
  product?: {
    title: string;
    price_cash: number;
  };
  bank_details?: {
    bank_name: string;
    branch_number: string;
    account_number_masked: string;
  };
}

export interface LeadStats {
  new: number;
  waiting_eligibility: number;
  approved: number;
  rejected: number;
  funded: number;
  closed: number;
}

export const leadsApi = {
  list: (params?: { project_id?: string; stage?: string; limit?: string; offset?: string }) =>
    api.get<{ leads: Lead[]; pagination: { total: number; hasMore: boolean } }>('/leads', params),
  
  get: (id: string) => api.get<{ lead: Lead }>(`/leads/${id}`),
  
  updateStage: (id: string, stage: string, notes?: string) =>
    api.post(`/leads/${id}/stage`, { stage, notes }),
  
  markEnteredHot: (id: string) => api.post(`/leads/${id}/entered-hot`),
  
  setEligibility: (id: string, approved: boolean, notes?: string) =>
    api.post(`/leads/${id}/eligibility`, { approved, notes }),
  
  revealData: (id: string) => api.post<{ 
    phone: string; 
    national_id?: string;
    bank_name?: string;
    branch_number?: string;
    account_number?: string;
  }>(`/leads/${id}/reveal`),
  
  stats: (projectId?: string) =>
    api.get<LeadStats>('/leads/stats', projectId ? { project_id: projectId } : undefined),
};

// ============================================
// Messages API
// ============================================

export interface Message {
  id: string;
  conversation_id: string;
  direction: 'in' | 'out';
  type: string;
  body: string;
  status: string;
  sent_at: string;
}

export interface Conversation {
  id: string;
  lead_id: string;
  contact_phone_masked: string;
  contact_name: string;
  last_message_at: string;
  unread_count: number;
  summary?: string;
}

export const messagesApi = {
  listConversations: (params?: { project_id?: string; limit?: string }) =>
    api.get<{ conversations: Conversation[] }>('/conversations', params),
  
  getMessages: (conversationId: string, params?: { limit?: string }) =>
    api.get<{ messages: Message[] }>(`/conversations/${conversationId}/messages`, params),
  
  send: (conversationId: string, text: string) =>
    api.post('/messages/send', { conversation_id: conversationId, text }),
  
  getAI: (conversationId: string) =>
    api.get<{ 
      summary: { summary: string; key_points: string[] };
      suggestions: { suggestions: string[] };
    }>(`/conversations/${conversationId}/ai`),
  
  extractData: (conversationId: string) =>
    api.post<{ extracted: Record<string, unknown>; confidence: Record<string, number> }>(
      `/conversations/${conversationId}/extract`
    ),
  
  confirmExtraction: (conversationId: string, confirmed: Record<string, unknown>) =>
    api.post(`/conversations/${conversationId}/extract/confirm`, { confirmed }),
};

// ============================================
// Order Packs API
// ============================================

export interface OrderPack {
  id: string;
  lead_id: string;
  product_title: string;
  price_cash: number;
  installments: number;
  tier: 'A' | 'B';
  monthly_payment: number;
  total_price: number;
  customer_name: string;
  payment_method: string;
  entered_in_hot: boolean;
  created_at: string;
  national_id_masked?: string;
  bank_name?: string;
  branch_number?: string;
  account_number_masked?: string;
}

export const orderPacksApi = {
  get: (leadId: string) => api.get<{ orderPack: OrderPack }>(`/order-packs/leads/${leadId}/order-pack`),
  
  create: (leadId: string, data: Partial<OrderPack>) =>
    api.post(`/order-packs/leads/${leadId}/order-pack`, data),
  
  update: (leadId: string, data: Partial<OrderPack>) =>
    api.patch(`/order-packs/leads/${leadId}/order-pack`, data),
  
  getText: (leadId: string) =>
    api.get<{ text: string }>(`/order-packs/leads/${leadId}/order-pack/text`),
};

// ============================================
// Projects API
// ============================================

export interface Project {
  id: string;
  name: string;
  waba_id: string;
}

export const projectsApi = {
  list: () => api.get<{ projects: Project[] }>('/projects'),
  get: (id: string) => api.get<{ project: Project }>(`/projects/${id}`),
};

// ============================================
// Reports API
// ============================================

export const reportsApi = {
  sendManual: (type: 'daily' | 'weekly' | 'monthly') =>
    api.post('/reports/send', { type }),
};

// ============================================
// Dashboard API
// ============================================

export interface DashboardStats {
  leadsByStage: Record<string, number>;
  conversionRates: {
    eligibility_approval_rate: number;
    funding_rate: number;
  };
  today: {
    newLeads: number;
    messages: number;
  };
  pendingAlerts: number;
  updatedAt: string;
}

export const dashboardApi = {
  stats: (projectId?: string) =>
    api.get<DashboardStats>('/dashboard/stats', projectId ? { project_id: projectId } : undefined),
};

